// ═══════════════════════════════════════════════════════════════════════════════
// Hub-Native Analyst Engineering Request — Floating Action Button
//
// Persistent low-profile FAB visible site-wide to authenticated users with
// analyst role or higher, gated by FEATURE_ANALYST_ENGINEERING_FAB.
// Clicking opens the AnalystRequestModal with the current location captured
// as `submissionContext.referringRoute`.
// ═══════════════════════════════════════════════════════════════════════════════
import { useState } from 'react';
import { theme } from '../../lib/theme';
import { useApp } from '../../hooks/useAppState';
import { FEATURE_ANALYST_ENGINEERING_FAB } from '../../config/featureFlags';
import { AnalystRequestModal } from './AnalystRequestModal';

export function AnalystRequestFab() {
  const { state } = useApp();
  const [open, setOpen] = useState(false);

  // Hard gate: flag off → render nothing.
  if (!FEATURE_ANALYST_ENGINEERING_FAB) return null;

  // Auth gate: only show when we have a real user. The blank user has empty id.
  if (!state.user?.id) return null;

  // Role gate: any authenticated non-blank user with a role qualifies as
  // analyst+ per spec §9 #8 ("all authenticated non-engineer roles").
  const role = state.user.role;
  if (!role) return null;

  // Capture current route from the address bar so the modal can prefill
  // submissionContext.referringRoute. The Hub uses hash-style routing with
  // `?v=<viewId>` query params, so we use location.pathname + location.search
  // + location.hash to capture the user's actual position.
  const referringRoute = typeof window !== 'undefined'
    ? `${window.location.pathname}${window.location.search}${window.location.hash}`
    : '/';

  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        title="Request engineering help"
        aria-label="Request engineering help"
        data-testid="analyst-eng-fab"
        style={{
          position: 'fixed',
          right: 20,
          bottom: 20,
          zIndex: 90,
          background: theme.accent,
          color: '#fff',
          border: 'none',
          borderRadius: 28,
          padding: '12px 18px',
          fontSize: 12,
          fontWeight: 700,
          letterSpacing: 0.2,
          cursor: 'pointer',
          boxShadow: '0 6px 18px rgba(0,0,0,0.28)',
          display: 'flex',
          alignItems: 'center',
          gap: 8,
        }}
      >
        <span style={{ fontSize: 14 }}>🔧</span>
        <span>Engineering help</span>
      </button>
      {open && (
        <AnalystRequestModal
          referringRoute={referringRoute}
          onClose={() => setOpen(false)}
        />
      )}
    </>
  );
}
